import React, { useState, useCallback, useRef, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { useMsal } from "@azure/msal-react";
import {
  Box,
  TextField,
  Switch,
  FormControlLabel,
  Tooltip,
} from "@mui/material";
import DynamicTabPanel from "../TabPanels/DynamicTabPanel/DynamicTabPanel";
import LoadBoardFilters from "../LoadBoards/LoadBoard/LoadBoardFilters/LoadBoardFilters";
import LoadBoardColumns from "../LoadBoards/LoadBoard/LoadBoardColumns/LoadBoardColumns";
import LoadBoardV2 from "./LoadBoardV2/LoadBoardV2";
import { CustomLoadBoardService } from "../../services/CustomLoadBoardService";
import { ActiveUserContext, AlertContext } from "../Dashboard/Dashboard";
import { formatTitleCase } from "../../utils/formatHelpers";
import { sortArrayOfNumbersAsc } from "../../utils/sortHelpers";

// Default columns configuration - matches LoadBoardColumns format
// title = display label, columnName = data key, isChecked = visibility, displayOrder = column order
// trackingOrder = order for Tracking tab (only columns with trackingOrder show on Tracking tab)
// width = fixed column width matching V1
const DEFAULT_COLUMNS = [
  { title: "Status", columnName: "brokerageStatus", type: "status", width: 100, isChecked: true, displayOrder: 1, trackingOrder: 6 },
  { title: "Load", columnName: "orderId", type: "loadId", width: 96, isChecked: true, displayOrder: 2, trackingOrder: 4 },
  { title: "Carrier", columnName: "carrierName", type: "carrier", idKey: "carrierId", width: 220, isChecked: true, displayOrder: 3, trackingOrder: 3 },
  { title: "Order", columnName: "orderTypeId", type: "default", width: 100, isChecked: true, displayOrder: 4, trackingOrder: 7 },
  { title: "Tender", columnName: "tenderType", type: "default", width: 100, isChecked: true, displayOrder: 5 },
  { title: "Customer", columnName: "customerName", type: "default", width: 280, isChecked: true, displayOrder: 6, trackingOrder: 19 },
  { title: "Mode", columnName: "mode", type: "mode", width: 160, isChecked: true, displayOrder: 7, trackingOrder: 21 },
  { title: "Origin City", columnName: "originCity", type: "uppercase", width: 130, isChecked: true, displayOrder: 8, trackingOrder: 14 },
  { title: "OS", columnName: "originState", type: "default", width: 80, isChecked: true, displayOrder: 9, trackingOrder: 15 },
  { title: "Pick Up", columnName: "originSchedArriveEarly", type: "datetime", lateKey: "originSchedArriveLate", stopType: "Origin", width: 140, isChecked: true, displayOrder: 10, trackingOrder: 13 },
  { title: "Dest City", columnName: "destinationCity", type: "uppercase", width: 130, isChecked: true, displayOrder: 11, trackingOrder: 17 },
  { title: "DS", columnName: "destinationState", type: "default", width: 80, isChecked: true, displayOrder: 12, trackingOrder: 18 },
  { title: "Delivery", columnName: "destinationSchedArriveEarly", type: "datetime", lateKey: "destinationSchedArriveLate", stopType: "Destination", width: 140, isChecked: true, displayOrder: 13, trackingOrder: 16 },
  { title: "Miles", columnName: "miles", type: "miles", width: 90, isChecked: true, displayOrder: 14, trackingOrder: 22 },
  { title: "Weight", columnName: "weight", type: "weight", width: 100, isChecked: true, displayOrder: 15 },
  { title: "Commodity", columnName: "commodity", type: "titlecase", width: 130, isChecked: true, displayOrder: 16 },
  { title: "Customer Rate", columnName: "customerTotalCharges", type: "currency", width: 150, isChecked: true, displayOrder: 17 },
  { title: "Carrier Pay", columnName: "carrierPay", type: "currency", isChecked: true, displayOrder: 18 },
  { title: "Gross Profit", columnName: "grossProfit", type: "currency", isChecked: true, displayOrder: 19 },
  { title: "Max Pay", columnName: "maxPay", type: "maxPay", width: 160, isChecked: true, displayOrder: 20 },
  { title: "Offers", columnName: "offerCount", type: "offers", width: 120, isChecked: true, displayOrder: 21 },
  { title: "Carrier Rep", columnName: "distpatcherUserId", type: "carrierRep", width: 130, isChecked: true, displayOrder: 22, trackingOrder: 2 },
  { title: "Acct Mgr", columnName: "accountMgr", type: "default", width: 120, isChecked: true, displayOrder: 23, trackingOrder: 20 },
  { title: "Rate Con", columnName: "rateConfirmationStatus", type: "default", width: 110, isChecked: true, displayOrder: 24 },
  { title: "Rate Con Sent", columnName: "rateConfirmationSentDate", type: "datetime", width: 140, isChecked: true, displayOrder: 25, trackingOrder: 23 },
  { title: "ETA to Next Stop", columnName: "newEta", type: "datetime", width: 160, isChecked: true, displayOrder: 26, trackingOrder: 11 },
  { title: "ETA Status", columnName: "etaStatus", type: "etaStatus", width: 130, isChecked: true, displayOrder: 27, trackingOrder: 10 },
  { title: "Next Stop", columnName: "nextStop", type: "default", width: 140, isChecked: true, displayOrder: 28 },
  { title: "Last Callin Date", columnName: "lastCallDateTime", type: "datetime", width: 150, isChecked: true, displayOrder: 29, trackingOrder: 1 },
  { title: "Current Location", columnName: "currentLocation", type: "default", width: 150, isChecked: true, displayOrder: 30, trackingOrder: 8 },
  { title: "Tracking Status", columnName: "trackStatus", type: "default", width: 150, isChecked: true, displayOrder: 31, trackingOrder: 5 },
  { title: "Next Scheduled Check Call", columnName: "nextSchedCall", type: "datetime", width: 180, isChecked: true, displayOrder: 32, trackingOrder: 12 },
  { title: "Distance To Next Stop", columnName: "distanceNextStop", type: "miles", width: 170, isChecked: true, displayOrder: 33, trackingOrder: 9 },
  { title: "BOL #", columnName: "blnum", type: "default", isChecked: true, displayOrder: 5 },
];

// Default filters for "All" tab (60 days back, 30 days out)
const DEFAULT_ALL_FILTERS = [
  { fieldName: "daysBack", params: "60" },
  { fieldName: "daysOut", params: "30" }
];

// Tracking tab statuses
const TRACKING_STATUSES = ["COV", "COVCOM", "DEL", "DIS", "LOA", "LOAD"];

export default function LoadBoardsV2({ isAdminUser, isLoadBoardLoading, allLoads, deleteNewLoads, onLoadFieldUpdate, handleTableAction, useLoadBoardsV2, setUseLoadBoardsV2 }) {
  const { instance } = useMsal();
  const { warning } = useContext(AlertContext);
  const activeUser = useContext(ActiveUserContext);

  // Tab state
  const [isLoading, setIsLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [tabLabels, setTabLabels] = useState([
    { customLoadBoardByUserID: "All", customLoadBoardName: "All", disableDelete: true },
    { customLoadBoardByUserID: "Tracking", customLoadBoardName: "Tracking", disableDelete: true },
  ]);
  const [newTabAdded, setNewTabAdded] = useState(false);
  const [tabDeleted, setTabDeleted] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_ALL_FILTERS);
  const [columns, setColumns] = useState(DEFAULT_COLUMNS);

  // Search state
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const searchTimeoutRef = useRef(null);

  // Total results (updated by child)
  const [totalResults, setTotalResults] = useState(0);

  // Load custom tabs for user
  useEffect(() => {
    if (activeUser?.userId) {
      setIsLoading(true);
      CustomLoadBoardService.GetCustomLoadBoardsByUser(instance, activeUser.userId)
        .then(res => {
          if (res.length > 0) {
            const tabs = [...tabLabels];
            res.filter(({ isActive }) => isActive).forEach(board => {
              tabs.push(board);
            });
            setTabLabels(tabs);
          }
          setIsLoading(false);
        })
        .catch(error => {
          console.error(error);
          setIsLoading(false);
          warning(error);
        });
    }
  }, [activeUser?.userId]);

  // Handle tab change
  const handleTabChange = useCallback((newValue) => {
    setIsLoading(true);
    setTabValue(newValue);
    setSearchInput("");
    setSearch("");

    if (newValue === 0) {
      // "All" tab - default filters and columns
      setFilters(DEFAULT_ALL_FILTERS);
      setColumns(DEFAULT_COLUMNS);
    } else if (newValue === 1) {
      // "Tracking" tab - specific filters
      let regionName = activeUser?.region?.regionName;
      regionName = regionName === "Great Plains" ? "Plains States" : formatTitleCase(regionName?.replace(/\s+/g, ""));

      setFilters([
        { fieldName: "daysBack", params: "60" },
        { fieldName: "daysOut", params: "30" },
        { fieldName: "brokerageStatus", params: TRACKING_STATUSES.join("||") },
        { fieldName: "etaStatus", params: "-1||1||2||3||4" },
        { fieldName: isAdminUser ? "destinationZoneName" : "distpatcherUserId", params: isAdminUser ? regionName : activeUser?.userIdMcld }
      ]);

      // Tracking tab - show only columns with trackingOrder, sorted by trackingOrder
      const defCols = DEFAULT_COLUMNS
        .filter(({ trackingOrder }) => !trackingOrder)
        .map(defCol => ({ ...defCol, isChecked: false }));

      let trackingColumns = DEFAULT_COLUMNS.filter(({ trackingOrder }) => trackingOrder);
      trackingColumns = sortArrayOfNumbersAsc(trackingColumns, "trackingOrder");
      trackingColumns = trackingColumns.concat(defCols);
      setColumns(trackingColumns);
    } else {
      // Custom tab - use saved filters and columns
      const customFilters = tabLabels[newValue]?.customLoadBoardByUserFilters || [];
      setFilters(customFilters.length > 0 ? customFilters : DEFAULT_ALL_FILTERS);

      // Load saved columns for custom tab
      let savedColumns = tabLabels[newValue]?.customLoadBoardByUserColumns || [];
      if (savedColumns.length > 0) {
        const defCols = DEFAULT_COLUMNS
          .filter(({ columnName }) => !savedColumns.some(col => col.columnName === columnName))
          .map(defCol => ({ ...defCol, isChecked: false }));

        savedColumns = sortArrayOfNumbersAsc(savedColumns, "displayOrder");
        savedColumns = savedColumns.concat(defCols);
        savedColumns.forEach((col, i) => {
          const defCol = DEFAULT_COLUMNS.find(({ columnName }) => columnName === col.columnName);
          if (defCol) {
            // Preserve user's visibility preference
            const userIsChecked = col.isChecked;
            
            // Copy ALL properties from DEFAULT_COLUMNS (type, lateKey, stopType, width, etc.)
            Object.assign(col, defCol);
            
            // Restore user's visibility preference and set display order based on position
            col.isChecked = userIsChecked;
            col.displayOrder = i + 1;
          }
        });
        setColumns(savedColumns);
      } else {
        setColumns(DEFAULT_COLUMNS);
      }
    }
    setIsLoading(false);
  }, [activeUser, isAdminUser, tabLabels]);

  // Handle tab create
  const handleTabCreate = useCallback((name) => {
    const payload = {
      userObjectId: activeUser.userId,
      customLoadBoardName: name,
      isActive: true,
      customLoadBoardByUserFilters: filters,
      customLoadBoardByUserColumns: columns
    };

    setIsLoading(true);
    CustomLoadBoardService.CreateCustomLoadBoard(instance, payload)
      .then(res => {
        const tabs = [...tabLabels];
        localStorage.setItem("newTabCreated", true);
        localStorage.setItem("newTabCreated2", true);
        tabs.push(res);
        setTabLabels(tabs);
        setNewTabAdded(true);
        setIsLoading(false);
      })
      .catch(error => {
        console.error(error);
        setIsLoading(false);
        warning(error);
      });
  }, [activeUser, filters, columns, tabLabels, instance, warning]);

  // Handle tab delete
  const handleTabDelete = useCallback((index) => {
    const payload = tabLabels[index];
    payload.isActive = false;

    setIsLoading(true);
    CustomLoadBoardService.UpdateCustomLoadBoard(instance, payload)
      .then(() => {
        localStorage.setItem("deletedTab", true);
        if (localStorage.getItem(`loadboard-tab-${payload.customLoadBoardByUserID}`)) {
          localStorage.removeItem(`loadboard-tab-${payload.customLoadBoardByUserID}`);
        }
        if (localStorage.getItem(`sorting-tab-${payload.customLoadBoardByUserID}`)) {
          localStorage.removeItem(`sorting-tab-${payload.customLoadBoardByUserID}`);
        }

        const tabs = [...tabLabels];
        tabs.splice(index, 1);
        setTabLabels(tabs);
        setTabDeleted(index);
        setIsLoading(false);
      })
      .catch(error => {
        console.error(error);
        setIsLoading(false);
        warning(error);
      });
  }, [tabLabels, instance, warning]);

  // Handle new tab navigation
  useEffect(() => {
    if (newTabAdded) {
      handleTabChange(tabLabels.length - 1);
      setNewTabAdded(false);
    }
  }, [newTabAdded, tabLabels.length, handleTabChange]);

  // Handle tab deletion navigation
  useEffect(() => {
    if (tabDeleted !== null) {
      setTabDeleted(null);
      if (tabDeleted === tabValue) handleTabChange(0);
      else if (tabDeleted < tabValue) handleTabChange(tabValue - 1);
    }
  }, [tabDeleted, tabValue, handleTabChange]);

  // Save custom tab filters and columns when they change
  useEffect(() => {
    if (tabValue > 1 && tabLabels[tabValue]) {
      const payload = { ...tabLabels[tabValue] };
      payload.customLoadBoardByUserFilters = filters;
      payload.customLoadBoardByUserColumns = columns;

      CustomLoadBoardService.UpdateCustomLoadBoard(instance, payload)
        .then(res => {
          const tabs = [...tabLabels];
          tabs[tabValue] = res;
          setTabLabels(tabs);
        })
        .catch(error => {
          console.error(error);
          warning(error);
        });
    }
  }, [filters, columns]);

  // Debounced search handler - only applies after 3+ characters with 300ms delay
  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setSearchInput(value);

    // Clear any existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Debounce the actual search filter
    searchTimeoutRef.current = setTimeout(() => {
      // Only apply search if 3+ characters or empty (to clear)
      if (value.length >= 3 || value.length === 0) {
        setSearch(value);
      }
    }, 300);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Box sx={{ p: 3, height: "calc(100vh - 100px)", display: "flex", flexDirection: "column" }}>
      {/* Tabs Row */}
      <Box sx={{ display: "flex", borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Box sx={{ display: "flex", flexGrow: 1 }}>
          <DynamicTabPanel
            isLoading={isLoading || isLoadBoardLoading}
            tabLabels={tabLabels}
            value={tabValue}
            nameKey="customLoadBoardName"
            handleTabChange={handleTabChange}
            handleTabCreate={handleTabCreate}
            handleTabDelete={handleTabDelete}
            maxTabs={7}
          />
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {tabValue !== 1 && (
            <LoadBoardColumns
              tabValue={tabValue}
              columns={columns}
              setColumns={setColumns}
              defaultColumns={DEFAULT_COLUMNS}
            />
          )}
          <LoadBoardFilters
            activeUser={activeUser}
            isAdminUser={isAdminUser}
            tabValue={tabValue}
            allLoads={allLoads}
            filters={filters}
            setFilters={setFilters}
          />
          <TextField
            size="small"
            placeholder={`Search ${totalResults} results (min 3 chars)...`}
            value={searchInput}
            onChange={handleSearchChange}
            sx={{ width: 300 }}
          />
          <Tooltip title="Switch between LoadBoards V1 and V2">
            <FormControlLabel
              control={
                <Switch
                  checked={useLoadBoardsV2}
                  onChange={(e) => {
                    const newValue = e.target.checked;
                    setUseLoadBoardsV2(newValue);
                    localStorage.setItem("useLoadBoardsV2", JSON.stringify(newValue));
                  }}
                  size="small"
                />
              }
              label="V2"
              sx={{ ml: 1 }}
            />
          </Tooltip>
        </Box>
      </Box>

      {/* Child LoadBoardV2 component */}
      <LoadBoardV2
        isAdminUser={isAdminUser}
        tabValue={tabValue}
        tabLabels={tabLabels}
        isLoading={isLoading || isLoadBoardLoading}
        customFilters={filters}
        customColumns={columns}
        search={search}
        allLoads={allLoads}
        deleteNewLoads={deleteNewLoads}
        onLoadFieldUpdate={onLoadFieldUpdate}
        handleTableAction={handleTableAction}
        setTotalResults={setTotalResults}
      />
    </Box>
  );
}

LoadBoardsV2.propTypes = {
  isAdminUser: PropTypes.bool,
  isLoadBoardLoading: PropTypes.bool.isRequired,
  allLoads: PropTypes.array.isRequired,
  deleteNewLoads: PropTypes.func.isRequired,
  onLoadFieldUpdate: PropTypes.func,
  handleTableAction: PropTypes.func.isRequired,
  useLoadBoardsV2: PropTypes.bool,
  setUseLoadBoardsV2: PropTypes.func,
};